/**
 * SchoolSwitcher — top-bar control for picking which school every
 * school-scoped screen resolves to (ux-overhaul-hld.md §3; Slice 2,
 * docs/design/completeness-lld.md).
 *
 * Visibility rule (the §3 fix — the switcher used to render nothing for a
 * concrete multi-school claim, hiding it from exactly the org admins who
 * most need it):
 *  - Wildcard scope, or a concrete claim with more than one school → the
 *    dropdown, options = the resolved `schools` list from schoolSelection.ts.
 *  - Exactly one resolvable school → a static label (school name, no
 *    chevron, not focusable) — the viewer has no choice to make, but every
 *    school-scoped page still answers "which school am I looking at".
 *  - Zero resolvable schools → renders nothing.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, School as SchoolIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthV2Store } from '@/features/auth/authV2Store';
import { useSchoolSelectionStore, useSelectedSchoolId } from './schoolSelection';

export function SchoolSwitcher() {
  const { schoolId, selectedSchool, schools, canSwitch, isResolving } = useSelectedSchoolId();
  const setSelectedSchool = useSchoolSelectionStore((s) => s.setSelectedSchool);
  const orgId = useAuthV2Store((s) => s.user?.org_id);

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (schools.length === 0 && !selectedSchool?.name) return null;

  const currentSchool = schools.find((school) => school.id === schoolId) ?? selectedSchool;
  const label = currentSchool?.name || (isResolving ? 'Loading…' : 'Select school');

  // Exactly one resolvable school: static label, nothing to switch between.
  if (!canSwitch) {
    return (
      <span
        data-testid="school-switcher"
        className="flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-500"
      >
        <SchoolIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
        <span className="max-w-[180px] truncate font-medium text-gray-700">{label}</span>
      </span>
    );
  }

  function handleSelect(school: { id: string; name: string }) {
    setSelectedSchool(orgId ? { ...school, orgId } : school);
    setIsOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        data-testid="school-switcher"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={isResolving}
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm transition-colors',
          'hover:border-blue-300 hover:bg-blue-50/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
          isResolving && 'cursor-not-allowed opacity-60',
        )}
      >
        <SchoolIcon className="h-3.5 w-3.5 flex-shrink-0 text-blue-600" />
        <span className="max-w-[180px] truncate font-medium text-gray-900">{label}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute right-0 top-full z-50 mt-1.5 max-h-72 w-64 overflow-auto rounded-xl border border-gray-200 bg-white p-1 shadow-lg"
        >
          {schools.map((school) => {
            const isSelected = school.id === schoolId;
            return (
              <button
                key={school.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-testid={`school-switcher-option-${school.id}`}
                onClick={() => handleSelect(school)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                  isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50',
                )}
              >
                <span className="truncate">{school.name}</span>
                {isSelected && <Check className="h-3.5 w-3.5 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
